import { handleRequest } from '../../axios';
import { axiosInstance } from './JsdhNotifikaceService';
import {
  ADMIN_HYDRANT_BODY_URL, ADMIN_HYDRANT_BODY_BY_ID_URL, ADMIN_HYDRANT_BODY_MAPA_URL,
  ADMIN_HYDRANT_BODY_STATISTIKY_URL, ADMIN_HYDRANT_IMPORT_GEOJSON_URL, ADMIN_HYDRANT_IMPORT_GPX_URL,
  ADMIN_HYDRANT_CHECKLIST_URL, ADMIN_HYDRANT_KONTROLA_URL, ADMIN_HYDRANT_KONTROLY_URL,
  ADMIN_HYDRANT_KONTROLA_FOTO_URL, ADMIN_HYDRANT_PLANY_URL, ADMIN_HYDRANT_PLAN_BY_ID_URL,
  ADMIN_HYDRANT_ZAVADY_URL, ADMIN_HYDRANT_ZAVADA_BY_ID_URL, ADMIN_HYDRANT_EXPORT_CSV_URL,
  ADMIN_HYDRANT_BODY_QR_IMAGE_URL, ADMIN_HYDRANT_KONTROLA_PDF_URL, ADMIN_HYDRANT_PLAN_REPORT_PDF_URL,
  ADMIN_HYDRANT_QR_STITKY_URL, ADMIN_HYDRANT_PLAN_POLOZKA_PRIRAZENI_URL,
} from '../url';
import { downloadFile } from '../../../utils/downloadFile';
import {
  HydrantBod, CreateHydrantRequest, UpdateHydrantRequest, HydrantMapResponse,
  HydrantDetailResponse, HydrantStatistiky, ChecklistPolozka, CreateHydrantKontrolaRequest,
  HydrantKontrolaResponse, HydrantPlan, HydrantPlanResponse, CreateHydrantPlanRequest, HydrantZavada,
  UpdateHydrantZavadaRequest, ImportHydrantyResponse,
} from '../generated/models/HydrantBod';

const stahniBlob = async (
  url: string,
  filename: string,
  accept: string,
  params?: Record<string, unknown>,
): Promise<void> => {
  const res = await axiosInstance.get(url, {
    responseType: 'blob',
    headers: { Accept: accept },
    ...(params ? { params } : {}),
  });
  downloadFile(res.data, filename);
};

export const HydrantBodService = {
  getAll: () =>
    handleRequest<HydrantBod[]>(() => axiosInstance.get(ADMIN_HYDRANT_BODY_URL)),
  getDetail: (id: number) =>
    handleRequest<HydrantDetailResponse>(() => axiosInstance.get(ADMIN_HYDRANT_BODY_BY_ID_URL(id))),
  create: (data: CreateHydrantRequest) =>
    handleRequest<HydrantBod>(() => axiosInstance.post(ADMIN_HYDRANT_BODY_URL, data)),
  update: (id: number, data: UpdateHydrantRequest) =>
    handleRequest<HydrantBod>(() => axiosInstance.put(ADMIN_HYDRANT_BODY_BY_ID_URL(id), data)),
  remove: (id: number) =>
    handleRequest<void>(() => axiosInstance.delete(ADMIN_HYDRANT_BODY_BY_ID_URL(id))),
  getMapa: () =>
    handleRequest<HydrantMapResponse[]>(() => axiosInstance.get(ADMIN_HYDRANT_BODY_MAPA_URL)),
  getStatistiky: () =>
    handleRequest<HydrantStatistiky>(() => axiosInstance.get(ADMIN_HYDRANT_BODY_STATISTIKY_URL)),
  importGeoJson: (geojson: string) =>
    handleRequest<ImportHydrantyResponse>(() =>
      axiosInstance.post(ADMIN_HYDRANT_IMPORT_GEOJSON_URL, { geojson })),
  importGpx: (gpx: string) =>
    handleRequest<ImportHydrantyResponse>(() =>
      axiosInstance.post(ADMIN_HYDRANT_IMPORT_GPX_URL, { gpx })),
  getChecklist: (id: number) =>
    handleRequest<ChecklistPolozka[]>(() => axiosInstance.get(ADMIN_HYDRANT_CHECKLIST_URL(id))),
  provestKontrolu: (id: number, data: CreateHydrantKontrolaRequest) =>
    handleRequest<HydrantKontrolaResponse>(() =>
      axiosInstance.post(ADMIN_HYDRANT_KONTROLA_URL(id), data)),
  getKontroly: (id: number) =>
    handleRequest<HydrantKontrolaResponse[]>(() => axiosInstance.get(ADMIN_HYDRANT_KONTROLY_URL(id))),
  uploadFoto: (kontrolaId: number, file: File, popis?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (popis) form.append('popis', popis);
    return handleRequest<unknown>(() =>
      axiosInstance.post(ADMIN_HYDRANT_KONTROLA_FOTO_URL(kontrolaId), form,
        { headers: { 'Content-Type': 'multipart/form-data' } }));
  },
  getPlany: () =>
    handleRequest<HydrantPlanResponse[]>(() => axiosInstance.get(ADMIN_HYDRANT_PLANY_URL)),
  getPlanDetail: (planId: number) =>
    handleRequest<HydrantPlanResponse>(() => axiosInstance.get(ADMIN_HYDRANT_PLAN_BY_ID_URL(planId))),
  createPlan: (data: CreateHydrantPlanRequest) =>
    handleRequest<HydrantPlan>(() => axiosInstance.post(ADMIN_HYDRANT_PLANY_URL, data)),
  getZavady: () =>
    handleRequest<HydrantZavada[]>(() => axiosInstance.get(ADMIN_HYDRANT_ZAVADY_URL)),
  updateZavada: (id: number, data: UpdateHydrantZavadaRequest) =>
    handleRequest<HydrantZavada>(() => axiosInstance.put(ADMIN_HYDRANT_ZAVADA_BY_ID_URL(id), data)),

  // Stažení souborů — VŽDY přes axiosInstance (Bearer token přidává request interceptor).
  // NIKDY přes <a href> — token je v localStorage, ne v cookie → 401/403 bez Authorization hlavičky.
  stahniCsv: () => stahniBlob(ADMIN_HYDRANT_EXPORT_CSV_URL, 'hydranty.csv', 'text/csv'),
  stahniKontrolaPdf: (kontrolaId: number) =>
    stahniBlob(ADMIN_HYDRANT_KONTROLA_PDF_URL(kontrolaId), `protokol-kontroly-${kontrolaId}.pdf`, 'application/pdf'),
  stahniPlanReportPdf: (planId: number) =>
    stahniBlob(ADMIN_HYDRANT_PLAN_REPORT_PDF_URL(planId), `souhrn-kontrol-${planId}.pdf`, 'application/pdf'),
  stahniQrImage: (hydrantId: number, oznaceni?: string) =>
    stahniBlob(ADMIN_HYDRANT_BODY_QR_IMAGE_URL(hydrantId), `qr-${oznaceni || hydrantId}.png`, 'image/png'),
  priraditKontrolora: (polozkaId: number, data: { clenId?: number; jmeno?: string }) =>
    handleRequest<void>(() => axiosInstance.put(ADMIN_HYDRANT_PLAN_POLOZKA_PRIRAZENI_URL(polozkaId), data)),
  stahniQrStitky: (ids: number[]) =>
    stahniBlob(ADMIN_HYDRANT_QR_STITKY_URL, 'qr-stitky.pdf', 'application/pdf', { ids: ids.join(',') }),
};
